/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { lazy } from 'react';

/**
 * The parts of the app that are fetched on demand, and the prefetch that stops
 * that being a downgrade.
 *
 * **Why this is its own file.** `AppLayout` needs `preloadViews`, and the router
 * needs `AppLayout` — putting both in `router.tsx` makes an import cycle. It
 * would resolve today and break the first time module evaluation order shifted,
 * which is exactly the kind of failure that arrives during a deploy rather than
 * during a test.
 *
 * **What is split.** The sign-in screen — which carries `LoginArt`, the largest
 * component in the app at 600 lines and of no use whatsoever to somebody already
 * signed in — and the four views that are not the board.
 *
 * **The two task modals are split as well, and that was not the first answer.**
 * Leaving them in looked right — a chunk fetched on a card click puts a wait in
 * the one place somebody is already waiting — but the build disagreed: the modals
 * pull in the whole task editor, and measured, that is 36KB gzipped sitting in
 * the chunk everybody downloads to look at a board. Splitting them and fetching
 * them first in the idle pass gets both.
 *
 * **What is not split: the board.** It is where every session starts, so
 * deferring it moves the wait rather than removing it.
 */
const load = {
  login: () => import('@/routes/LoginView'),
  taskModal: () => import('@/task/TaskModal'),
  newTaskModal: () => import('@/task/NewTaskModal'),
  list: () => import('@/views/ListView'),
  calendar: () => import('@/views/CalendarView'),
  myDay: () => import('@/views/MyDayView'),
  archived: () => import('@/views/ArchivedView'),
};

export const LoginView = lazy(() => load.login().then((m) => ({ default: m.LoginView })));
export const TaskModal = lazy(() => load.taskModal().then((m) => ({ default: m.TaskModal })));
export const NewTaskModal = lazy(() => load.newTaskModal().then((m) => ({ default: m.NewTaskModal })));
export const ListView = lazy(() => load.list().then((m) => ({ default: m.ListView })));
export const CalendarView = lazy(() => load.calendar().then((m) => ({ default: m.CalendarView })));
export const MyDayView = lazy(() => load.myDay().then((m) => ({ default: m.MyDayView })));
export const ArchivedView = lazy(() => load.archived().then((m) => ({ default: m.ArchivedView })));

/**
 * Pull the other views down once the browser has nothing better to do.
 *
 * Without this, splitting trades a faster first paint for a stutter the first
 * time somebody presses List — a worse deal, because the first paint happens
 * once and the view switch happens all day. `requestIdleCallback` keeps it from
 * competing with the board's own first render.
 *
 * Failures are swallowed on purpose: a prefetch that never arrives costs
 * nothing, because the route imports it again when it is actually needed.
 */
export function preloadViews(): void {
  const run = () => {
    // The modals first, deliberately. Opening a task is the likeliest next thing
    // anybody does from a board, and it is the only one of these where the
    // fallback would appear over work already on screen.
    for (const get of [load.taskModal, load.newTaskModal, load.list, load.calendar, load.myDay, load.archived]) {
      void get().catch(() => {});
    }
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}
